import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Menu,
  MenuItem,
  Box,
  IconButton,
  Avatar,
  Container,
  useTheme,
  useMediaQuery,
  Modal,
  TextField,
  CircularProgress
} from '@mui/material';
import {
  Menu as MenuIcon,
  Person,
  ExitToApp,
  Settings,
  LightMode,
  DarkMode,
  Assistant,
  Close
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme as useAppTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { clearTokens } from '../services/authService';
import { journeyRecommendation } from '../api/home';
import { useHomeContent } from '../contexts/HomeContentContext';

const NavBar = ({ user }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [assistantModalOpen, setAssistantModalOpen] = useState(false);
  const [assistantInput, setAssistantInput] = useState('');
  const [suggestion, setSuggestion] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode, toggleTheme } = useAppTheme();
  const { setUser } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { updateFeaturedFromAssistant } = useHomeContent();

  const handleAccountMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseAccountMenu = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleCloseAccountMenu();
    clearTokens();
    setUser(null);
    navigate('/', { replace: true });
  };

  const handleOpenAssistant = () => {
    setAssistantModalOpen(true);
  };

  const handleCloseAssistant = () => {
    setAssistantModalOpen(false);
    setAssistantInput('');
    setSuggestion('');
    setLoading(false);
  };

  const handleSubmitAssistant = async () => {
    if (!assistantInput.trim()) return;
    
    setLoading(true);
    
    // Hardcoded response for v1 - will be replaced with AI API call
    try {
      const response = await journeyRecommendation(assistantInput);
      console.log(response);
      setSuggestion(response.reason + " Your homepage has been updated with the recommended content.");

      // Push featured updates to Home context if provided
      updateFeaturedFromAssistant({
        affirmation: response.affirmation,
        video: response.video,
        audio: response.audio,
      });
    } catch (error) {

      const msg = error?.response?.data?.Error || "I'm having trouble connecting right now. Please try exploring our Video, Audio, or EBooks sections for content that resonates with you.";
      setSuggestion(msg.replace("OperationResult failed", "").replaceAll("\"", ""));
    } finally {
      setLoading(false);
    }
  };

  const NavigationLinks = () => (
    <>
      <Button color="inherit" onClick={() => navigate('/home')} sx={{ mx: 1 }}>
        Home
      </Button>
      <Button color="inherit" onClick={() => navigate('/video')} sx={{ mx: 1 }}>
        Video
      </Button>
      <Button color="inherit" onClick={() => navigate('/audio')} sx={{ mx: 1 }}>
        Audio
      </Button>
      <Button color="inherit" onClick={() => navigate('/ebooks')} sx={{ mx: 1 }}>
        EBooks
      </Button>
      <Button color="inherit" onClick={() => navigate('/journal')} sx={{ mx: 1 }}>
        Journal
      </Button>
      <Button color="inherit" onClick={() => navigate('/products')} sx={{ mx: 1 }}>
        Shop
      </Button>
    </>
  );

  return (
    <AppBar position="sticky" elevation={0}>
      <Toolbar>
        <Typography
          variant="h6"
          component="div"
          sx={{
            flexGrow: isMobile ? 1 : 0,
            fontFamily: "'Playfair Display', serif",
            fontWeight: 600,
            color: theme.palette.primary.main,
            mr: 4,
            cursor: 'pointer',
            userSelect: 'none'
          }}
          onClick={() => navigate('/home')}
        >
          Waves Within
        </Typography>

        {/* Desktop Navigation */}
        {!isMobile && (
          <Box sx={{ flexGrow: 1, display: 'flex' }}>
            <NavigationLinks />
          </Box>
        )}

        {/* Mobile Menu Button */}
        {isMobile && (
          <IconButton
            color="inherit"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Assistant Button */}
        <IconButton
          onClick={handleOpenAssistant}
          sx={{ ml: 1 }}
          color="inherit"
          aria-label="wellness assistant"
        >
          <Assistant />
        </IconButton>

        {/* Theme Toggle Button */}
        <IconButton
          onClick={toggleTheme}
          sx={{ ml: 1 }}
          color="inherit"
          aria-label="toggle theme"
        >
          {mode === 'dark' ? <LightMode /> : <DarkMode />}
        </IconButton>

        {/* Account Menu */}
        <IconButton
          onClick={handleAccountMenu}
          sx={{ ml: 2 }}
        >
          <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
            {user?.name ? user.name.charAt(0) : '?'}
          </Avatar>
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseAccountMenu}
        >
          <MenuItem onClick={() => { handleCloseAccountMenu(); navigate('/profile'); }}>
            <Person sx={{ mr: 1 }} /> Profile
          </MenuItem>
          <MenuItem onClick={() => { handleCloseAccountMenu(); navigate('/settings'); }}>
            <Settings sx={{ mr: 1 }} /> Settings
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ExitToApp sx={{ mr: 1 }} /> Logout
          </MenuItem>
        </Menu>
      </Toolbar>

      {/* Mobile Navigation Menu */}
      {isMobile && mobileMenuOpen && (
        <Box sx={{ bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
          <Container>
            <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button onClick={() => navigate('/video')} fullWidth sx={{ justifyContent: 'flex-start' }}>
                Video
              </Button>
              <Button onClick={() => navigate('/audio')} fullWidth sx={{ justifyContent: 'flex-start' }}>
                Audio
              </Button>
              <Button onClick={() => navigate('/ebooks')} fullWidth sx={{ justifyContent: 'flex-start' }}>
                EBooks
              </Button>
              <Button onClick={() => navigate('/journal')} fullWidth sx={{ justifyContent: 'flex-start' }}>
                Journal
              </Button>
              <Button onClick={() => navigate('/shop')} fullWidth sx={{ justifyContent: 'flex-start' }}>
                Shop
              </Button>
            </Box>
          </Container>
        </Box>
      )}

      {/* Assistant Modal */}
      <Modal
        open={assistantModalOpen}
        onClose={handleCloseAssistant}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            maxWidth: 600,
            width: '100%',
            mx: 'auto',
            boxShadow: 8,
            p: 4,
            position: 'relative',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}
        >
          <IconButton
            onClick={handleCloseAssistant}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'text.secondary'
            }}
          >
            <Close />
          </IconButton>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" fontWeight={600} color="primary" gutterBottom>
              🌊 Wellness Assistant
            </Typography>
            <Typography variant="body1" color="text.secondary">
              How do you feel? What do you need?
            </Typography>
          </Box>

          <TextField
            fullWidth
            multiline
            rows={4}
            value={assistantInput}
            onChange={(e) => setAssistantInput(e.target.value)}
            placeholder="Tell me about how you're feeling or what kind of support you're looking for today..."
            variant="outlined"
            sx={{ mb: 3 }}
            disabled={loading}
          />

          {suggestion && !loading && (
            <Box
              sx={{
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(144, 224, 239, 0.1)' : 'rgba(144, 224, 239, 0.15)',
                borderRadius: 2,
                p: 3,
                mb: 3,
                border: `1px solid ${theme.palette.primary.main}30`
              }}
            >
              <Typography variant="h6" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Assistant fontSize="small" />
                Suggestion for you:
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                {suggestion}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleCloseAssistant}
              disabled={loading}
            >
              Close
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitAssistant}
              disabled={loading || !assistantInput.trim()}
              sx={{ minWidth: 120 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Get Suggestion'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </AppBar>
  );
};

export default NavBar; 